<script lang="ts" setup>
/**
 * @link https://idsk.gov.sk/komponenty/tlacidla
 * @link https://github.com/id-sk/id-sk-frontend/tree/master/src/idsk/components/button
 */
import { buttonVariants, CLASS_ROOT } from './constants'
import { computed, ref } from 'vue'

export type ButtonVariant = typeof buttonVariants[number]

export interface IDSKButtonProps {
  href?: string
  /** Displays arrow on the right. */
  isStartButton?: boolean
  element?: 'a' | 'button' | 'input'
  variant?: ButtonVariant
  preventDoubleClick?: boolean
  text?: string
  disabled?: boolean
  type?: 'submit' | 'button'
  dataTestId?: string
}

const DEBOUNCE_TIMEOUT_IN_SECONDS = 1

const emit = defineEmits(['click'])

const props = withDefaults(defineProps<IDSKButtonProps>(), {
  variant: 'primary',
  element: 'button',
  href: undefined,
  text: undefined,
  preventDoubleClick: false,
  type: 'button',
  dataTestId: '',
})
const classNames = computed(() => [
  CLASS_ROOT,
  `${CLASS_ROOT}--${props.variant}`,
  {
    [`${CLASS_ROOT}--disabled`]: props.disabled,
    [`${CLASS_ROOT}--start`]: props.isStartButton,
  },
])

const debounceFormSubmitTimer = ref<ReturnType<typeof setTimeout> | null>()

const handleKeyDown = (event: KeyboardEvent) => {
  // get the target element
  const target = event.target
  // if the element has a role='button' and the pressed key is a space, we'll simulate a click
  if (target instanceof HTMLElement && target.getAttribute('role') === 'button') {
    event.preventDefault()
    // trigger the target's click event
    target.click()
  }
}

const handleClick = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof HTMLElement)) {
    emit('click', event)
    return
  }

  // Check the button that is clicked on has the preventDoubleClick feature enabled
  if (target.getAttribute('data-prevent-double-click') !== 'true') {
    emit('click', event)
    return
  }

  // If the timer is still running then we want to prevent the click from submitting the form
  if (debounceFormSubmitTimer.value) {
    event.preventDefault()
    return
  }

  debounceFormSubmitTimer.value = setTimeout(() => {
    debounceFormSubmitTimer.value = null
  }, DEBOUNCE_TIMEOUT_IN_SECONDS * 1000)

  emit('click', event)
}
</script>

<template>
  <a
    v-if="element === 'a' || props.href"
    v-bind="$attrs"
    :href="href"
    :class="classNames"
    :aria-disabled="disabled"
    :data-prevent-double-click="preventDoubleClick"
    role="button"
    draggable="false"
    :data-test-id="
      props.dataTestId ? props.dataTestId + '-' + 'button-link' : 'button-link'
    "
    @click="handleClick"
    @keydown.space="handleKeyDown"
  >
    <slot></slot>
    <IDSKButtonArrow v-if="isStartButton" />
  </a>
  <button
    v-else-if="element === 'button'"
    v-bind="$attrs"
    :class="classNames"
    :aria-disabled="disabled"
    :data-prevent-double-click="preventDoubleClick"
    :disabled="disabled"
    :type="type"
    :data-test-id="props.dataTestId ? props.type + '-' + props.dataTestId : props.type"
    @click="handleClick"
    @keydown.space="handleKeyDown"
  >
    <slot></slot>
    <IDSKButtonArrow v-if="isStartButton" />
  </button>
  <input
    v-else-if="element === 'input'"
    v-bind="$attrs"
    :class="classNames"
    :value="props.text"
    :aria-disabled="disabled"
    :disabled="disabled"
    :type="type"
    :data-test-id="props.dataTestId ? props.type + '-' + props.dataTestId : props.type"
    @click="emit('click')"
  />
</template>

<style scoped lang="scss">
.idsk-button {
  border-radius: var(--radius-small);
}
.idsk-button--search {
  background: var(--idsk-color-blue);
  &:hover,
  &:focus {
    background: var(--idsk-color-dark-blue);
  }
  &:active {
    background: var(--idsk-color-dark-blue);
  }
}
</style>
